import type { BackendClient } from '@playbooks/core/src/services/BackendClient'
import {
  AiTeammateRealtimeModel,
  AiTeammateTranscriptionLanguage,
  Section,
} from '@playbooks/core/src/interfaces/ActionCard'
import { getRmsLevelDb, SpeechSegmentDetector } from './speechSegmentDetector'
import { RealtimeStatsCollector, type RealtimeUsage } from './realtimeStatsCollector'

/**
 * Subset of OpenAI Realtime server events consumed by the AI Teammate widget.
 */
export interface TeammateRealtimeEvent {
  type: string
  response?: {
    id?: string
    usage?: RealtimeUsage
    output?: Array<{
      type?: string
      name?: string
      arguments?: string
    }>
  }
  /** Conversation item of transcription events */
  item_id?: string
  /** Transcription delta text */
  delta?: string
  transcript?: string
  usage?: RealtimeUsage
  session?: {
    audio?: { input?: { turn_detection?: unknown } }
  }
  error?: {
    type?: string
    code?: string
    message?: string
  }
}

/**
 * Coarse connection state emitted to React so the component can render controls.
 */
export type TeammateRealtimeStatus = 'connecting' | 'listening' | 'stopped'

/**
 * Stable error codes mapped to localized widget strings by the React layer.
 */
export type TeammateRealtimeErrorCode = 'audioUnavailable' | 'connectionLost'

/**
 * Per-session OpenAI Realtime creation options sent to the backend.
 */
export interface TeammateRealtimeSessionConfig {
  actionCardSections: string[]
  currentDate?: string
  recordTranscript?: boolean
  transcriptionLanguage?: AiTeammateTranscriptionLanguage
  fillAutomatically?: boolean
  model?: AiTeammateRealtimeModel
}

/**
 * Dependencies and callbacks required by {@link TeammateRealtimeController}.
 */
interface TeammateRealtimeControllerParams {
  backendClient: BackendClient
  recordTranscript?: boolean
  transcriptionLanguage?: AiTeammateTranscriptionLanguage
  fillAutomatically?: boolean
  model?: AiTeammateRealtimeModel
  getErrorMessage: (code: TeammateRealtimeErrorCode) => string
  onEvent: (event: TeammateRealtimeEvent) => void
  onStatusChange: (status: TeammateRealtimeStatus) => void
  onError: (message: string) => void
}

// Session will be recreated after this duration to proactively rotate before OpenAI's 60-minute limit
const SESSION_ROTATION_MS = 55 * 60 * 1000
const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 15000
/** Max time to wait for the final transcripts of committed speech when stopping */
const STOP_TRANSCRIPTION_TIMEOUT_MS = 3000
/** Audio frame size analyzed by the client-side VAD, in samples (~85ms at 48kHz) */
const VAD_FRAME_SIZE = 4096

/** Events with the final transcript (or its failure) of a committed conversation item */
const finalTranscriptEvents = new Set([
  'conversation.item.input_audio_transcription.completed',
  'conversation.item.input_audio_transcription.failed',
])

/**
 * Client-side voice activity detection, committing speech segments in sessions without server turn detection
 */
interface SpeechDetection {
  audioContext: AudioContext
  detector: SpeechSegmentDetector
}

/**
 * Owns OpenAI Realtime WebRTC lifecycle for the AI Teammate widget.
 *
 * The controller intentionally keeps browser connection objects, reconnect timers,
 * session rotation, and stats collection out of React state. React receives only
 * coarse status updates and parsed Realtime events.
 */
export class TeammateRealtimeController {
  /** Active WebRTC peer connection for the current OpenAI Realtime session. */
  private peerConnection: RTCPeerConnection | null = null
  /** OpenAI Realtime event channel associated with the current peer connection. */
  private dataChannel: RTCDataChannel | null = null
  /** Audio stream currently being sent to OpenAI; retained so reconnect can reuse it. */
  private mediaStream: MediaStream | null = null
  /** Stats collector for the current internal backend session ID. */
  private statsCollector: RealtimeStatsCollector | null = null
  /** Timer that proactively rotates before OpenAI's 60-minute session limit. */
  private sessionRotationTimer: ReturnType<typeof setTimeout> | null = null
  /** Backoff timer for accidental disconnects or failed reconnect attempts. */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** Monotonic token used to ignore events from stale peer connections. */
  private connectionGeneration = 0
  /** Number of consecutive reconnect attempts since the last successful connection. */
  private reconnectAttempts = 0
  /**
   * Desired high-level state: true while the user wants AI Teammate to keep listening.
   * This stays true across planned rotation and transient reconnects, unlike
   * `isConnecting`, which only describes the current WebRTC attempt.
   */
  private shouldKeepListening = false
  /**
   * Distinguishes user-initiated stops from unexpected WebRTC closes. Connection
   * close handlers use this to avoid scheduling reconnect after an intentional stop.
   */
  private isManualDisconnect = false
  /** True while an SDP exchange or reconnect attempt is already in progress. */
  private isConnecting = false
  /** Latest raw Action Card sections used to build the next backend session config. */
  private actionCardSections: Section[] = []
  /**
   * Client-side VAD of transcript-only sessions, started along with the audio stream,
   * so it knows the background noise level before the user speaks.
   */
  private speechDetection: SpeechDetection | null = null
  /**
   * True if the session has no server turn detection (transcript-only sessions with streaming transcription models),
   * so speech segments detected by the VAD have to be committed by the client to receive completed transcripts.
   */
  private needsClientCommits = false
  /** True if speech was committed by the client in the current session */
  private hasCommittedSpeech = false
  /** Number of commits sent but not acknowledged yet (as committed, or rejected as empty) */
  private unacknowledgedCommits = 0
  /** Committed conversation items still waiting for their final transcript */
  private pendingTranscriptItemIds = new Set<string>()
  /** In-flight stop, shared by concurrent stop calls (e.g. a double click) */
  private stopPromise: Promise<void> | null = null
  /** Resolves the wait for the final transcripts when stopping */
  private resolveFinalTranscripts: (() => void) | null = null

  /**
   * Create a controller with backend and UI callback dependencies.
   */
  constructor(private readonly params: TeammateRealtimeControllerParams) {}

  /**
   * Store the latest raw Action Card sections for future Realtime session creation.
   */
  setActionCardSections(actionCardSections: Section[]) {
    this.actionCardSections = actionCardSections
  }

  /**
   * Start sending the provided audio stream to a new OpenAI Realtime session.
   */
  async start(stream: MediaStream) {
    // Let an in-flight stop finish first, so its delayed cleanup can't close the new session
    await this.stopPromise
    this.shouldKeepListening = true
    this.isManualDisconnect = false
    this.reconnectAttempts = 0
    this.mediaStream = stream
    if (!this.params.fillAutomatically) {
      try {
        this.startSpeechDetection(stream)
      } catch (err) {
        // Without the VAD, no completed transcripts arrive in sessions without server turn detection
        console.error('Error starting speech detection:', err)
      }
    }
    await this.connectWebRTC(stream)
  }

  /**
   * Stop listening, finalize stats, close WebRTC, and notify React that listening stopped.
   * Pending speech is transcribed first, so the last utterance before stopping is not lost.
   * Concurrent calls share the same stop.
   */
  async stop() {
    this.stopPromise ??= (async () => {
      await this.commitPendingSpeech()
      await this.stopInternal(true)
    })().finally(() => {
      this.stopPromise = null
    })
    await this.stopPromise
  }

  /**
   * Dispose resources without emitting React status updates.
   *
   * Use this from unmount paths where updating React state after cleanup would be unsafe.
   */
  async dispose() {
    await this.stopInternal(false)
  }

  /**
   * Return whether the controller is meant to keep listening across reconnects.
   */
  isActive() {
    return this.shouldKeepListening
  }

  private async stopInternal(emitStatus: boolean) {
    this.stopSpeechDetection()
    this.shouldKeepListening = false
    this.isManualDisconnect = true
    this.isConnecting = false
    this.clearReconnectTimer()
    this.clearSessionRotationTimer()
    this.cleanupConnection()
    await this.stopStatsCollector()
    this.mediaStream = null
    if (emitStatus) {
      this.params.onStatusChange('stopped')
    }
  }

  private async connectWebRTC(stream: MediaStream) {
    // Capture a generation for this connection attempt. Any later cleanup or
    // reconnect increments the shared generation and invalidates these handlers.
    const connectionGeneration = this.connectionGeneration + 1
    this.connectionGeneration = connectionGeneration
    this.isConnecting = true
    this.params.onStatusChange('connecting')

    const pc = new RTCPeerConnection()
    this.peerConnection = pc

    pc.addEventListener('connectionstatechange', () => {
      if (connectionGeneration !== this.connectionGeneration) return
      if (pc.connectionState === 'failed') {
        this.scheduleReconnect('connection failed')
      } else if (pc.connectionState === 'disconnected') {
        this.scheduleReconnect('connection disconnected')
      }
    })

    pc.addEventListener('iceconnectionstatechange', () => {
      if (connectionGeneration !== this.connectionGeneration) return
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        this.scheduleReconnect(`ICE ${pc.iceConnectionState}`)
      }
    })

    stream.getTracks().forEach(track => pc.addTrack(track, stream))

    // OpenAI Realtime server events arrive over this data channel.
    const dataChannel = pc.createDataChannel('oai-events')
    this.dataChannel = dataChannel
    this.setupDataChannelListeners(dataChannel, connectionGeneration)

    // The backend owns OpenAI API credentials and exchanges our SDP offer for
    // OpenAI's SDP answer plus an internal stats session ID.
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)

    const { sdpAnswer, sessionId } = await this.params.backendClient.createTeammateSession({
      sdp: offer.sdp!,
      ...this.buildSessionConfig(),
    })

    await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer })

    // A newer connection superseded this one while the async SDP exchange was
    // in flight. Close this peer and leave the current connection untouched.
    if (connectionGeneration !== this.connectionGeneration) {
      pc.close()
      return
    }

    // Stats are per OpenAI Realtime session. Finalize the previous session
    // before recording usage events against the newly-created backend row.
    if (sessionId > 0) {
      await this.stopStatsCollector()
      this.statsCollector = new RealtimeStatsCollector(this.params.backendClient)
      this.statsCollector.start(sessionId)
    }

    this.reconnectAttempts = 0
    this.clearReconnectTimer()
    this.startSessionRotationTimer()
    this.isConnecting = false
    this.params.onStatusChange('listening')
  }

  private setupDataChannelListeners(dataChannel: RTCDataChannel, connectionGeneration: number) {
    dataChannel.onopen = () => {
      if (connectionGeneration !== this.connectionGeneration) return
      console.log('Data channel opened')
    }

    dataChannel.onclose = () => {
      if (connectionGeneration !== this.connectionGeneration) return
      console.log('Data channel closed')
      this.scheduleReconnect('data channel closed')
    }

    dataChannel.onerror = err => {
      if (connectionGeneration !== this.connectionGeneration) return
      console.error('Data channel error:', err)
      this.scheduleReconnect('data channel error')
    }

    dataChannel.onmessage = event => {
      if (connectionGeneration !== this.connectionGeneration) return
      try {
        const message = JSON.parse(event.data) as TeammateRealtimeEvent
        // Usage must be recorded before handing the event to business logic so
        // parsing/tool-call errors in React do not drop valid usage stats.
        this.statsCollector?.record(message)
        this.trackSpeechCommits(message)
        this.params.onEvent(message)
      } catch (err) {
        console.error('Error parsing message:', err)
      }
    }
  }

  /**
   * Commit speech segments in sessions without server turn detection: once the session turns out to have
   * no turn detection, each speech segment detected by the client-side VAD is committed when it ends,
   * which makes OpenAI send its completed transcript.
   */
  private trackSpeechCommits(event: TeammateRealtimeEvent) {
    if (event.type === 'session.created' || event.type === 'session.updated') {
      this.needsClientCommits = !event.session?.audio?.input?.turn_detection
      return
    }

    // Track the committed speech until its final transcript arrives
    if (event.type === 'input_audio_buffer.committed') {
      this.unacknowledgedCommits = Math.max(this.unacknowledgedCommits - 1, 0)
      if (event.item_id) this.pendingTranscriptItemIds.add(event.item_id)
    } else if (event.type === 'error' && event.error?.code === 'input_audio_buffer_commit_empty') {
      this.unacknowledgedCommits = Math.max(this.unacknowledgedCommits - 1, 0)
    } else if (finalTranscriptEvents.has(event.type) && event.item_id) {
      this.pendingTranscriptItemIds.delete(event.item_id)
    }
    if (!this.hasPendingTranscripts()) this.resolveFinalTranscripts?.()
  }

  /** Whether committed speech is still waiting for its final transcript */
  private hasPendingTranscripts() {
    return this.unacknowledgedCommits > 0 || this.pendingTranscriptItemIds.size > 0
  }

  /**
   * Start the client-side VAD on the given audio stream.
   * Runs on the audio thread (ScriptProcessorNode), which, unlike timers, isn't throttled in background tabs.
   */
  private startSpeechDetection(stream: MediaStream) {
    const audioContext = new AudioContext()
    const detector = new SpeechSegmentDetector()
    const processor = audioContext.createScriptProcessor(VAD_FRAME_SIZE, 1, 1)
    processor.onaudioprocess = event => {
      const samples = event.inputBuffer.getChannelData(0)
      const frameMs = (samples.length / audioContext.sampleRate) * 1000
      if (detector.process(getRmsLevelDb(samples), frameMs) && this.needsClientCommits) this.commitSpeech()
    }
    audioContext.createMediaStreamSource(stream).connect(processor)
    // The processor only runs when connected to the output (it outputs silence)
    processor.connect(audioContext.destination)
    void audioContext.resume()
    this.speechDetection = { audioContext, detector }
  }

  private stopSpeechDetection() {
    void this.speechDetection?.audioContext.close()
    this.speechDetection = null
  }

  /**
   * Commit the current speech segment
   */
  private commitSpeech() {
    this.speechDetection?.detector.reset()
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(JSON.stringify({ type: 'input_audio_buffer.commit' }))
      this.hasCommittedSpeech = true
      this.unacknowledgedCommits++
    }
  }

  /**
   * Commit the pending speech segment, if any, and wait (limited) for the final transcripts of all committed speech
   */
  private async commitPendingSpeech() {
    const detector = this.speechDetection?.detector
    if (!this.needsClientCommits || !detector || this.dataChannel?.readyState !== 'open') return

    // Without any commit so far, any sound counts: the user may have been speaking since the very first frame,
    // which the VAD then took for the background noise level
    if (detector.hasPendingSpeech || (!this.hasCommittedSpeech && detector.hasPendingSound)) this.commitSpeech()
    if (!this.hasPendingTranscripts()) return

    await new Promise<void>(resolve => {
      const timeout = setTimeout(resolve, STOP_TRANSCRIPTION_TIMEOUT_MS)
      this.resolveFinalTranscripts = () => {
        clearTimeout(timeout)
        resolve()
      }
    })
    this.resolveFinalTranscripts = null
  }

  private scheduleReconnect(reason: string) {
    // Only reconnect when the user still wants the session alive and another
    // connection attempt is not already running.
    if (!this.shouldKeepListening || this.isManualDisconnect || this.isConnecting) return
    if (!this.mediaStream || this.mediaStream.getTracks().every(track => track.readyState === 'ended')) {
      void this.handleReconnectFailure(this.params.getErrorMessage('audioUnavailable'))
      return
    }
    if (this.reconnectTimer) return

    this.clearSessionRotationTimer()
    const attempt = this.reconnectAttempts + 1
    if (attempt > MAX_RECONNECT_ATTEMPTS) {
      void this.handleReconnectFailure(this.params.getErrorMessage('connectionLost'))
      return
    }

    // Exponential backoff keeps repeated network failures from hammering the
    // backend/OpenAI while still recovering quickly from brief Wi-Fi drops.
    this.reconnectAttempts = attempt
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1), RECONNECT_MAX_DELAY_MS)
    console.warn(`Realtime session reconnect scheduled (${reason}), attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS}`)
    this.params.onStatusChange('connecting')

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.reconnectWebRTC(reason)
    }, delay)
  }

  private async reconnectWebRTC(reason: string) {
    // Only attempt to reconnect if the user still wants to listen and audio is available
    if (!this.shouldKeepListening || this.isManualDisconnect || !this.mediaStream) return

    try {
      console.info(`Reconnecting Realtime session: ${reason}`)
      this.isConnecting = true
      this.params.onStatusChange('connecting')
      // Reconnect replaces only the OpenAI connection. The browser audio stream
      // stays active so the user does not need to grant mic access again.
      this.cleanupConnection()
      await this.stopStatsCollector()
      await this.connectWebRTC(this.mediaStream)
    } catch (err) {
      console.error('Error reconnecting Realtime session:', err)
      this.isConnecting = false
      this.scheduleReconnect('previous reconnect attempt failed')
    }
  }

  private async handleReconnectFailure(message: string) {
    console.error(message)
    this.params.onError(message)
    await this.stop()
  }

  private buildSessionConfig(): TeammateRealtimeSessionConfig {
    return {
      actionCardSections: this.actionCardSections.map(section => JSON.stringify(section)),
      currentDate: this.getClientLocalDate(),
      recordTranscript: this.params.recordTranscript,
      transcriptionLanguage: this.params.transcriptionLanguage,
      fillAutomatically: this.params.fillAutomatically,
      model: this.params.model,
    }
  }

  private getClientLocalDate() {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${now.getFullYear()}-${month}-${day}`
  }

  private startSessionRotationTimer() {
    this.clearSessionRotationTimer()
    this.sessionRotationTimer = setTimeout(() => {
      // Rotate before the provider-enforced 60-minute cap so the disconnect
      // happens at a controlled time and goes through the normal reconnect path.
      this.scheduleReconnect('session rotation')
    }, SESSION_ROTATION_MS)
  }

  private clearSessionRotationTimer() {
    if (this.sessionRotationTimer) {
      clearTimeout(this.sessionRotationTimer)
      this.sessionRotationTimer = null
    }
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private cleanupConnection() {
    this.connectionGeneration += 1

    // Speech segments and commits belong to the closed session (the VAD keeps running on the same audio stream)
    this.needsClientCommits = false
    this.hasCommittedSpeech = false
    this.unacknowledgedCommits = 0
    this.pendingTranscriptItemIds.clear()
    this.speechDetection?.detector.reset()

    if (this.dataChannel) {
      // Detach handlers before close so intentional cleanup does not schedule
      // a reconnect through stale close/error callbacks.
      this.dataChannel.onopen = null
      this.dataChannel.onclose = null
      this.dataChannel.onerror = null
      this.dataChannel.onmessage = null
      this.dataChannel.close()
      this.dataChannel = null
    }

    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }
  }

  private async stopStatsCollector() {
    const statsCollector = this.statsCollector
    if (!statsCollector) return

    this.statsCollector = null
    await statsCollector.stop()
  }
}
